import {
  AlertTriangle,
  Ban,
  Clock,
  Construction,
  Droplets,
  ExternalLink,
  Layers,
  MapPin,
  Mountain,
  Route,
  Snowflake,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { HazardKind, HazardWarning, UiLang, TrafficAlert, TrafficSeverity } from "../lib/types";
import { StaleNote } from "./StaleNote";

interface Props {
  alerts: TrafficAlert[];
  /**
   * Uten eget kort og egen tittel, fordi noe utenfor har dem alt.
   *
   * Panelet ligger nå i en sammenleggbar seksjon som navngir det og viser
   * tellingen i hodet. Beholdt panelet sine egne, ville hver melding stått
   * under to like overskrifter — og en overskrift som gjentar seg selv leses
   * som støy, også av en skjermleser.
   */
  bare?: boolean;
  /** Naturfarevarsler fra NVE. Vises først — de er mer alvorlige. */
  hazards: HazardWarning[];
  /** Tidspunktet planen ble lagret, når den er hentet fra lagringen. */
  staleSince?: number | null;
  lang: UiLang;
  /** Åpner stoppet meldingen ligger nærmest. */
  onSelectStop?: (stopId: string) => void;
}

const HAZARD_ICONS: Record<HazardKind, LucideIcon> = {
  avalanche: Snowflake,
  landslide: Layers,
  flood: Droplets,
};

const ICONS: Record<TrafficSeverity, LucideIcon> = {
  closed: Ban,
  convoy: Clock,
  warning: AlertTriangle,
  roadwork: Construction,
};

/**
 * Hvor mange meldinger som faktisk sperrer veien.
 *
 * Veiarbeid teller ikke: 974 av 1247 meldinger i Norge er veiarbeid, og et
 * tall som alltid er høyt sier ingenting. Eksportert fordi kalleren bruker den
 * til å avgjøre om panelet skal stå åpent — se `openOnNarrow` i lib/fold.ts.
 * Å la kalleren telle selv ville vært to definisjoner av «sperrer», og de to
 * ville kommet i utakt.
 */
export function blockingCount(alerts: TrafficAlert[], hazards: HazardWarning[]): number {
  return (
    alerts.filter((alert) => alert.severity === "closed" || alert.severity === "convoy").length +
    hazards.length
  );
}

/**
 * Hva som faktisk er stengt langs ruten, nå.
 *
 * Appen hadde alt en vinteradvarsel per stopp: «Vikafjellet kan stenge eller
 * kolonnekjøres, normalt november til april». Den er sann og den er en årstid.
 * Den som skal over fjellet i morgen tidlig trenger noe annet — om veien er
 * åpen, og hvis den er kolonnekjørt, når kolonnen går.
 *
 * Panelet viser ingenting når det ikke er noe å vise. Det er et poeng: en
 * tom liste betyr klar vei, og en overskrift med «ingen meldinger» under ville
 * fått den reisende til å lese den hver gang for å finne ut det samme.
 *
 * Veiarbeid står nederst og dempet. Det er 974 av 1247 meldinger i Norge, og
 * de færreste av dem endrer en reiseplan — men et kjørefelt stengt gjennom
 * Bergen sentrum er verdt å vite når man har regnet med å komme fram.
 */
export function TrafficAlertsPanel({
  alerts,
  hazards,
  staleSince,
  lang,
  onSelectStop,
  bare = false,
}: Props) {
  const t = dictionaries[lang];
  if (alerts.length === 0 && hazards.length === 0) return null;

  /*
   * Kolonnekjøring, stengte veier og naturfare er grunnen panelet finnes.
   * Veiarbeid teller ikke: 974 av 1247 meldinger i Norge er veiarbeid, og et
   * tall som alltid er høyt sier ingenting.
   */
  const blocking = blockingCount(alerts, hazards);

  return (
    <section className={bare ? "traffic traffic--bare" : "card traffic"}>
      {!bare && (
        <div className="card-title">
          <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
          {t.trafficTitle}
          <span className="traffic-count">
            {blocking > 0 ? t.trafficBlocking(blocking) : t.trafficCount(alerts.length)}
          </span>
        </div>
      )}
      <p className="card-sub">{t.trafficSubtitle}</p>
      {staleSince ? <StaleNote savedAt={staleSince} lang={lang} /> : null}

      {/*
        Naturfaren står øverst. En stengt vei koster tid; et snøskredvarsel på
        nivå 3 gjelder turen man skulle gått, og det er en annen slags beskjed.
      */}
      {hazards.length > 0 && (
        <ul className="hazard-list">
          {hazards.map((hazard) => {
            const Icon = HAZARD_ICONS[hazard.kind];
            return (
              <li
                className={`hazard ${
                  hazard.level >= 4
                    ? "hazard--high"
                    : hazard.level === 3
                      ? "hazard--mid"
                      : "hazard--low"
                }`}
                key={`${hazard.kind}-${hazard.area}`}
              >
                <span className="hazard-icon">
                  <Icon size={15} />
                </span>
                <span className="hazard-body">
                  <span className="hazard-head">
                    <b>
                      {t.hazardKind[hazard.kind]} — {hazard.area}
                    </b>
                    <span className="hazard-level">{t.hazardLevel(hazard.level)}</span>
                  </span>
                  <small>{hazard.headline}</small>
                  {hazard.advice && <small className="hazard-advice">{hazard.advice}</small>}
                  <span className="traffic-meta">
                    <a href={hazard.url} target="_blank" rel="noreferrer">
                      {t.hazardSource} <ExternalLink size={10} />
                    </a>
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <ul className="traffic-list">
        {alerts.map((alert) => {
          const Icon = ICONS[alert.severity];
          return (
            <li className={`traffic-item traffic-item--${alert.severity}`} key={alert.id}>
              <span className="traffic-icon">
                <Icon size={14} />
              </span>

              <span className="traffic-body">
                <span className="traffic-head">
                  <b>
                    {alert.road && <span className="traffic-road">{alert.road}</span>}
                    {alert.location}
                  </b>
                  <span className="traffic-tags">
                    <span className={`traffic-severity traffic-severity--${alert.severity}`}>
                      {t.trafficSeverity[alert.severity]}
                    </span>
                    {/* En fjellovergang stenger brått, og det er verdt et eget merke. */}
                    {alert.onMountainPass && (
                      <span className="traffic-pass">
                        <Mountain size={9} /> {t.trafficMountainPass}
                      </span>
                    )}
                  </span>
                </span>

                <small>{alert.description}</small>

                {/*
                  Hvilken strekning meldingen ligger på.
                  
                  Dette er hele svaret på «hva betyr dette for meg». «Fv5352
                  stengt i Bergen» er en opplysning en tilreisende ikke kan
                  bruke; «stengt på strekningen Voss–Bergen» er grunnen til å
                  legge om dagen. Mangler strekningen, ligger meldingen i
                  nærheten uten å berøre ruta, og det står også.
                */}
                {alert.onLeg ? (
                  <span className="traffic-leg">
                    <Route size={11} />
                    <b>
                      {alert.onLeg.fromName} → {alert.onLeg.toName}
                    </b>
                    <span>{t.trafficOnRoute}</span>
                  </span>
                ) : (
                  <span className="traffic-leg traffic-leg--off">
                    <MapPin size={11} />
                    <span>{t.trafficOffRoute}</span>
                  </span>
                )}

                <span className="traffic-meta">
                  {onSelectStop ? (
                    <button
                      type="button"
                      className="traffic-near"
                      onClick={() => onSelectStop(alert.onLeg?.fromStopId ?? alert.nearestStopId)}
                    >
                      {alert.onLeg ? t.trafficFromRoad(alert.distanceKm) : t.trafficNear(alert.distanceKm)}
                    </button>
                  ) : (
                    <span className="traffic-near">{t.trafficNear(alert.distanceKm)}</span>
                  )}
                  {alert.until && <span>{t.trafficUntil(formatDay(alert.until, lang))}</span>}
                  {alert.url && (
                    <a href={alert.url} target="_blank" rel="noreferrer">
                      {t.trafficSource} <ExternalLink size={10} />
                    </a>
                  )}
                </span>
              </span>
            </li>
          );
        })}
      </ul>

      <p className="traffic-credit">
        {t.trafficCredit}
        {hazards.length > 0 && ` ${t.hazardCredit}`}
      </p>
    </section>
  );
}

/**
 * Datoen en melding gjelder til, kort.
 *
 * Vegvesenet setter ofte et sluttidspunkt langt inn i framtida for et veiarbeid
 * som har stått i to år. Datoen står derfor uten klokkeslett — «til 15.12.2027»
 * er en opplysning, «til 15.12.2027 23:59» later som den er presis.
 */
function formatDay(iso: string, lang: UiLang): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(lang === "no" ? "nb-NO" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
